import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { withAuth, useAuth } from "@/context/AuthContext";
import { Upload, Trash, FilePdf, FileText, Paperclip, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AttachmentsPanel({ entityType, entityId, dense = false }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/attachments", { params: { entity_type: entityType, entity_id: entityId } });
      setItems(r.data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Failed to load attachments");
    }
  }, [entityType, entityId]);

  useEffect(() => { if (entityId) load(); }, [entityId, load]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5MB per file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_type", entityType);
      fd.append("entity_id", entityId);
      await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const onFiles = async (fileList) => {
    for (const f of Array.from(fileList || [])) {
      // eslint-disable-next-line no-await-in-loop
      await upload(f);
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const canRemove = (att) => att.uploaded_by === user?.id || user?.role === "organization_owner";
  const isImg = (ct) => ct && ct.startsWith("image/");
  const humanSize = (b) => b < 1024 ? `${b} B` : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/1024/1024).toFixed(1)} MB`;

  return (
    <div data-testid={`attachments-${entityType}`} className="space-y-3">
      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); onFiles(e.dataTransfer.files); }}
        className={`flex items-center justify-center gap-2 py-4 px-3 rounded-md border border-dashed cursor-pointer text-sm ${dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-secondary/40"}`}
        data-testid="attachment-dropzone"
      >
        <Upload size={16} />
        <span>{busy ? "Uploading…" : "Drop files here or click to upload"}</span>
        <input type="file" multiple onChange={(e) => onFiles(e.target.files)} className="hidden" accept="image/*,application/pdf" />
      </label>

      <div className={`grid gap-2 ${dense ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
        {items.map((att) => (
          <div key={att.id} className="group relative rounded-md border border-border p-2 flex items-center gap-3" data-testid={`attachment-${att.id}`}>
            {isImg(att.content_type) ? (
              <img src={withAuth(att.url)} alt={att.filename} className="w-12 h-12 object-cover rounded bg-secondary shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded bg-secondary flex items-center justify-center shrink-0">
                {att.content_type === "application/pdf" ? <FilePdf size={22} weight="duotone" className="text-muted-foreground" /> : <FileText size={22} weight="duotone" className="text-muted-foreground" />}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate" title={att.filename}>{att.filename}</div>
              <div className="text-[10px] text-muted-foreground truncate">{humanSize(att.size)} · {att.uploaded_by_name}</div>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <a href={withAuth(att.url)} download={att.filename} target="_blank" rel="noreferrer"
                 className="p-1.5 rounded hover:bg-secondary text-muted-foreground" title="Download">
                <DownloadSimple size={13} />
              </a>
              {canRemove(att) && (
                <button onClick={() => remove(att)} className="p-1.5 rounded hover:bg-destructive/10 text-destructive" title="Delete">
                  <Trash size={13} />
                </button>
              )}
            </div>
          </div>
        ))}
        {!items.length && (
          <div className="col-span-full text-center text-xs text-muted-foreground py-4">
            <Paperclip size={16} className="mx-auto mb-2 opacity-40" />
            No files yet
          </div>
        )}
      </div>
    </div>
  );
}
